// Thumbnail Cache System with LRU eviction

import { useState, useEffect } from 'react';

interface CacheEntry {
  blob: Blob;
  url: string;
  lastAccessed: number;
  size: number;
}

interface ThumbnailCacheConfig {
  maxSize: number; // Maximum cache size in bytes
  maxAge: number; // Maximum age in milliseconds
  defaultSize: { width: number; height: number };
}

const DEFAULT_CONFIG: ThumbnailCacheConfig = {
  maxSize: 50 * 1024 * 1024, // 50MB
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
  defaultSize: { width: 128, height: 128 },
};

class ThumbnailCache {
  private cache: Map<string, CacheEntry> = new Map();
  private currentSize = 0;
  private config: ThumbnailCacheConfig;
  private generationQueue: Map<string, Promise<string>> = new Map();

  constructor(config: Partial<ThumbnailCacheConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.loadFromStorage();
  }

  // Generate cache key from asset path and size
  private getCacheKey(assetPath: string, width: number, height: number): string {
    return `${assetPath}:${width}x${height}`;
  }

  // Get thumbnail URL (returns cached or generates new)
  async getThumbnail(
    assetPath: string,
    width: number = this.config.defaultSize.width,
    height: number = this.config.defaultSize.height
  ): Promise<string> {
    const key = this.getCacheKey(assetPath, width, height);

    // Check cache
    const cached = this.cache.get(key);
    if (cached) {
      cached.lastAccessed = Date.now();
      return cached.url;
    }

    // Check if already generating
    const pending = this.generationQueue.get(key);
    if (pending) {
      return pending;
    }

    // Generate new thumbnail
    const generationPromise = this.generateThumbnail(assetPath, width, height, key);
    this.generationQueue.set(key, generationPromise);

    try {
      const url = await generationPromise;
      return url;
    } finally {
      this.generationQueue.delete(key);
    }
  }

  // Generate thumbnail using canvas
  private async generateThumbnail(
    assetPath: string,
    width: number,
    height: number,
    key: string
  ): Promise<string> {
    // Load image
    const img = new Image();
    img.crossOrigin = 'anonymous';

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load image: ${assetPath}`));
      img.src = assetPath;
    });

    // Create canvas and draw scaled image
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Calculate scaling to fit while maintaining aspect ratio
    const scale = Math.min(width / img.width, height / img.height);
    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;
    const x = (width - scaledWidth) / 2;
    const y = (height - scaledHeight) / 2;

    // Enable image smoothing for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Clear and draw
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

    // Convert to WebP blob for better compression
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create thumbnail blob'));
        }
      }, 'image/webp', 0.8);
    });

    // Create URL and cache
    const url = URL.createObjectURL(blob);

    this.addToCache(key, {
      blob,
      url,
      lastAccessed: Date.now(),
      size: blob.size,
    });

    return url;
  }

  // Add entry to cache with LRU eviction
  private addToCache(key: string, entry: CacheEntry): void {
    // Remove old entry if exists
    const existing = this.cache.get(key);
    if (existing) {
      URL.revokeObjectURL(existing.url);
      this.currentSize -= existing.size;
    }

    // Evict oldest entries if necessary
    while (this.currentSize + entry.size > this.config.maxSize && this.cache.size > 0) {
      this.evictOldest();
    }

    // Add new entry
    this.cache.set(key, entry);
    this.currentSize += entry.size;
  }

  // Evict oldest entry (LRU)
  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    for (const [key, entry] of this.cache) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      const entry = this.cache.get(oldestKey);
      if (entry) {
        URL.revokeObjectURL(entry.url);
        this.currentSize -= entry.size;
      }
      this.cache.delete(oldestKey);
    }
  }

  // Invalidate specific thumbnail
  invalidate(assetPath: string, width?: number, height?: number): void {
    if (width !== undefined && height !== undefined) {
      const key = this.getCacheKey(assetPath, width, height);
      const entry = this.cache.get(key);
      if (entry) {
        URL.revokeObjectURL(entry.url);
        this.currentSize -= entry.size;
        this.cache.delete(key);
      }
    } else {
      // Invalidate all sizes for this asset
      const keysToDelete: string[] = [];
      for (const [key, entry] of this.cache) {
        if (key.startsWith(assetPath + ':')) {
          URL.revokeObjectURL(entry.url);
          this.currentSize -= entry.size;
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.cache.delete(key));
    }
  }

  // Clear entire cache
  clear(): void {
    for (const entry of this.cache.values()) {
      URL.revokeObjectURL(entry.url);
    }
    this.cache.clear();
    this.currentSize = 0;
    this.saveToStorage();
  }

  // Get cache stats
  getStats(): { entries: number; size: number; maxSize: number } {
    return {
      entries: this.cache.size,
      size: this.currentSize,
      maxSize: this.config.maxSize,
    };
  }

  // Cleanup expired entries
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, entry] of this.cache) {
      if (now - entry.lastAccessed > this.config.maxAge) {
        URL.revokeObjectURL(entry.url);
        this.currentSize -= entry.size;
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  // Persist metadata to IndexedDB (simplified version uses localStorage)
  private async loadFromStorage(): Promise<void> {
    try {
      // Note: Full implementation would use IndexedDB for blob storage
      // This simplified version uses memory-only cache
      // IndexedDB implementation would be added for production use
    } catch (error) {
      console.warn('Failed to load thumbnail cache:', error);
    }
  }

  private async saveToStorage(): Promise<void> {
    try {
      // Note: Full implementation would persist to IndexedDB
      // This simplified version uses memory-only cache
    } catch (error) {
      console.warn('Failed to save thumbnail cache:', error);
    }
  }
}

// Singleton instance
export const thumbnailCache = new ThumbnailCache();

// Hook for using thumbnail cache in React components
export function useThumbnail(
  assetPath: string | null | undefined,
  width?: number,
  height?: number
): { url: string | null; loading: boolean; error: Error | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!assetPath) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    thumbnailCache
      .getThumbnail(assetPath, width, height)
      .then((thumbnailUrl) => {
        if (!cancelled) {
          setUrl(thumbnailUrl);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [assetPath, width, height]);

  return { url, loading, error };
}
